import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase.js';
import {
  applicationStatusLabel, companyKindLabel, count, countValue, formatDay, formatMoney, memberRoleLabel, idCheckLabel, idCheckTone, jobStatusLabel, loadPerson, orDash, personTypeLabel,
  repairStatusLabel, showTrades, signedUpAsLabel, workStatusLabel,
} from './adminData.js';
import { SortableTable } from './SortableTable.jsx';
import { labelOf } from './sorting.js';
import { formatWhen, idTypeLabels, rejectionReasonLabels } from './staffAdmin.js';

const list = (value) => (Array.isArray(value) ? value : []);
const yesNo = (value) => (value ? 'Yes' : 'No');
const when = (value) => orDash(value ? formatWhen(String(value)) : '');
const day = formatDay;
/** "business-owner" and "in_progress" read as "Business owner" and "In progress" when we don't have a better word. */
const plainWords = (value) => {
  if (value === null || value === undefined || value === '') return '–';
  const text = String(value).replace(/[-_]+/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const initial = { person: null, sensitive: false, error: '', busy: false };

function None() {
  return <p className="mod-note">None.</p>;
}

/** One of the person's lists as a table whose every column sorts, or "None." when it is empty. */
function ListTable({ columns, rows, rowKey = (item, index) => item.id || index }) {
  if (!rows.length) return <None/>;
  return <SortableTable columns={columns} rows={rows} rowKey={rowKey}/>;
}

const companyColumns = (onOpen) => [
  {
    key: 'company',
    label: 'Company',
    value: (company) => company.name,
    cell: (company) => (company.kind === 'supplier' && showTrades
      ? <button type="button" className="admin-link" onClick={() => onOpen({ tab: 'trades', trade: company.id })}>{orDash(company.name)}</button>
      : orDash(company.name)),
  },
  { key: 'kind', label: 'What it is', value: (company) => labelOf(company.kind, companyKindLabel), cell: (company) => companyKindLabel(company.kind) },
  {
    key: 'role',
    label: 'Role',
    value: (company) => labelOf(company.role, memberRoleLabel),
    cell: (company) => <>{memberRoleLabel(company.role)}{company.active === false ? ' (no longer active)' : ''}</>,
  },
  { key: 'verified', label: 'Verified', value: (company) => Boolean(company.verified), first: 'desc', cell: (company) => yesNo(company.verified) },
  { key: 'joined', label: 'Joined', value: (company) => company.joined_at, first: 'desc', cell: (company) => day(company.joined_at) },
];

const homeColumns = [
  { key: 'home', label: 'Home', value: (home) => home.label, cell: (home) => orDash(home.label) },
  { key: 'area', label: 'Area', value: (home) => home.area, cell: (home) => orDash(home.area) },
  { key: 'provider', label: 'Housing provider', value: (home) => home.provider, cell: (home) => orDash(home.provider) },
  { key: 'tenure', label: 'Tenancy details given', value: (home) => Boolean(home.tenure_given), first: 'desc', cell: (home) => yesNo(home.tenure_given) },
  { key: 'landlord', label: 'Landlord contact given', value: (home) => Boolean(home.landlord_contact_given), first: 'desc', cell: (home) => yesNo(home.landlord_contact_given) },
  { key: 'added', label: 'Added', value: (home) => home.added_at, first: 'desc', cell: (home) => day(home.added_at) },
  { key: 'archived', label: 'Archived', value: (home) => Boolean(home.archived), first: 'desc', cell: (home) => (home.archived ? 'Archived' : '–') },
];

const repairColumns = [
  { key: 'repair', label: 'Repair', value: (repair) => repair.title, cell: (repair) => orDash(repair.title) },
  { key: 'status', label: 'Status', value: (repair) => labelOf(repair.status, repairStatusLabel), cell: (repair) => repairStatusLabel(repair.status) },
  { key: 'urgency', label: 'Urgency', value: (repair) => labelOf(repair.urgency, plainWords), cell: (repair) => plainWords(repair.urgency) },
  { key: 'provider', label: 'Sent to', value: (repair) => repair.provider, cell: (repair) => orDash(repair.provider) },
  { key: 'reported', label: 'Reported', value: (repair) => repair.created_at, first: 'desc', cell: (repair) => day(repair.created_at) },
];

const homeJobColumns = [
  { key: 'job', label: 'Job', value: (job) => job.title, cell: (job) => orDash(job.title) },
  { key: 'status', label: 'Status', value: (job) => labelOf(job.status, jobStatusLabel), cell: (job) => jobStatusLabel(job.status) },
  { key: 'area', label: 'Area', value: (job) => job.location, cell: (job) => orDash(job.location) },
  { key: 'bids', label: 'Bids', value: (job) => countValue(job.bids), first: 'desc', cell: (job) => count(job.bids) },
  { key: 'posted', label: 'Posted', value: (job) => job.created_at, first: 'desc', cell: (job) => day(job.created_at) },
];

const hiredColumns = [
  { key: 'job', label: 'Job', value: (work) => work.title, cell: (work) => orDash(work.title) },
  { key: 'trade', label: 'Trade', value: (work) => work.trade, cell: (work) => orDash(work.trade) },
  { key: 'status', label: 'Status', value: (work) => labelOf(work.status, workStatusLabel), cell: (work) => workStatusLabel(work.status) },
  { key: 'value', label: 'Value', value: (work) => work.amount_minor, first: 'desc', cell: (work) => formatMoney(work.amount_minor, work.currency || 'GBP') },
  { key: 'started', label: 'Started', value: (work) => work.created_at, first: 'desc', cell: (work) => day(work.created_at) },
  { key: 'completed', label: 'Completed', value: (work) => work.completed_at, first: 'desc', cell: (work) => day(work.completed_at) },
];

const roomOf = (application) => [application.town, application.area].filter(Boolean).join(', ');
const applicationColumns = [
  { key: 'room', label: 'Room', value: roomOf, cell: (application) => orDash(roomOf(application)) },
  { key: 'status', label: 'Status', value: (application) => labelOf(application.status, applicationStatusLabel), cell: (application) => applicationStatusLabel(application.status) },
  { key: 'applied', label: 'Applied', value: (application) => application.created_at, first: 'desc', cell: (application) => day(application.created_at) },
];

const termColumns = [
  { key: 'version', label: 'Version', value: (term) => term.version, cell: (term) => orDash(term.version) },
  { key: 'accepted', label: 'Accepted', value: (term) => term.accepted_at, first: 'desc', cell: (term) => day(term.accepted_at) },
];

const viewColumns = [
  { key: 'who', label: 'Who', value: (view) => view.by, cell: (view) => orDash(view.by) },
  { key: 'opened', label: 'What they opened', value: (view) => (view.sensitive ? 'With private details' : 'Record only'), cell: (view) => (view.sensitive ? 'With private details' : 'Record only') },
  { key: 'when', label: 'When', value: (view) => view.viewed_at, first: 'desc', cell: (view) => when(view.viewed_at) },
];

/** Everything we hold on one person. Opening it is logged by the database; the private details only come back on request. */
export default function PersonRecord({ id, onOpen, backType }) {
  const [state, setState] = useState(initial);
  const latest = useRef(0);

  const load = useCallback(async (sensitive) => {
    const mine = ++latest.current;
    setState((current) => ({ ...current, busy: true, error: '' }));
    try {
      const person = await loadPerson(supabase, id, { sensitive });
      if (latest.current === mine) setState({ person, sensitive, error: '', busy: false });
    } catch (cause) {
      // The record we already have stays on screen, and the private details stay as they were.
      if (latest.current === mine) setState((current) => ({ ...current, error: cause.message, busy: false }));
    }
  }, [id]);

  useEffect(() => {
    setState({ ...initial, busy: true });
    void load(false);
    return () => { latest.current += 1; };
  }, [load]);

  const { person, sensitive, error, busy } = state;
  const back = () => onOpen({ tab: 'people', type: backType || 'all' });
  const backButton = <button type="button" className="button button-secondary" onClick={back}>← Back to people</button>;

  if (!person) {
    return (
      <>
        <div className="admin-record-head"><div className="admin-head-actions">{backButton}</div></div>
        {error ? <p className="mod-error" role="alert">{error}</p> : <p>Loading this person…</p>}
      </>
    );
  }

  const account = person.account || {};
  const types = list(person.types);
  const companies = list(person.companies);
  const homes = list(person.homes);
  const repairs = list(person.repairs);
  const homeJobs = list(person.home_jobs);
  const hired = list(person.work_hired);
  const search = person.homes_search || {};
  const applications = list(search.applications);
  const identity = person.identity || null;
  const passport = person.passport || {};
  const messages = person.messages || {};
  const safety = person.safety || {};
  const notifications = person.notifications || {};
  const terms = list(person.terms);
  const views = list(person.views);
  const hidden = person.private || null;
  const title = account.name || account.email || 'No name given';

  const housingAnswers = search.housing_answers_completed_at
    ? `Completed on ${day(search.housing_answers_completed_at)}`
    : (search.housing_answers_started ? 'Started' : 'Not started');

  return (
    <>
      <div className="admin-record-head">
        <div>
          <h2>{title}</h2>
          <span className="admin-badges">
            {types.map((key) => <span key={key} className={`admin-badge${key === 'staff' ? ' is-staff' : ''}`}>{personTypeLabel(key)}</span>)}
          </span>
        </div>
        <div className="admin-head-actions">
          {backButton}
          <button type="button" className="button button-secondary" disabled={busy} aria-busy={busy} onClick={() => void load(!sensitive)}>
            {sensitive ? 'Hide private details' : 'Show private details'}
          </button>
        </div>
      </div>
      <p className="mod-note">Opening private details is recorded.</p>
      {error && <p className="mod-error" role="alert">{error}</p>}

      {hidden && <PrivateDetails hidden={hidden} homes={homes} repairs={repairs} homeJobs={homeJobs}/>}

      <h2 className="admin-section">Account</h2>
      <dl className="mod-fields">
        <dt>Email</dt><dd>{orDash(account.email)}</dd>
        <dt>Joined</dt><dd>{day(account.joined_at)}</dd>
        <dt>Email confirmed</dt><dd>{account.email_confirmed_at ? day(account.email_confirmed_at) : 'Not yet'}</dd>
        <dt>Last signed in</dt><dd>{account.last_sign_in_at ? when(account.last_sign_in_at) : 'Never'}</dd>
        <dt>Signed up as</dt><dd>{signedUpAsLabel(account.signed_up_as)}</dd>
        <dt>Account type</dt><dd>{plainWords(account.account_type)}</dd>
        <dt>Staff role</dt><dd>{plainWords(account.staff_role)}</dd>
        <dt>Suspended</dt><dd>{yesNo(account.suspended)}</dd>
      </dl>

      <h2 className="admin-section">Companies</h2>
      <ListTable columns={companyColumns(onOpen)} rows={companies}/>

      <h2 className="admin-section">Homes</h2>
      <ListTable columns={homeColumns} rows={homes}/>

      <h2 className="admin-section">Repairs</h2>
      <ListTable columns={repairColumns} rows={repairs}/>

      <h2 className="admin-section">Home jobs they posted</h2>
      <ListTable columns={homeJobColumns} rows={homeJobs}/>

      <h2 className="admin-section">Work they've hired</h2>
      <ListTable columns={hiredColumns} rows={hired}/>

      <h2 className="admin-section">Looking for a home</h2>
      <dl className="mod-fields">
        <dt>Housing questions</dt><dd>{housingAnswers}</dd>
        <dt>Health consent</dt><dd>{search.health_consent_at ? day(search.health_consent_at) : 'Not given'}</dd>
        <dt>Saved rooms</dt><dd>{count(search.saved_rooms)}</dd>
      </dl>
      <ListTable columns={applicationColumns} rows={applications}/>
      <p className="mod-note">Their housing answers aren't shown: residents are told only they can see them.</p>

      <h2 className="admin-section">ID check</h2>
      {identity
        ? (
          <dl className="mod-fields">
            <dt>Status</dt><dd><span className={`admin-status is-${idCheckTone(identity.status)}`}>{idCheckLabel(identity.status)}</span></dd>
            <dt>Sent</dt><dd>{when(identity.submitted_at)}</dd>
            <dt>Decided</dt><dd>{identity.reviewed_at ? when(identity.reviewed_at) : 'Not yet'}</dd>
            {identity.rejection_reason && <><dt>Reason for rejecting</dt><dd>{rejectionReasonLabels[identity.rejection_reason] || plainWords(identity.rejection_reason)}</dd></>}
          </dl>
        )
        : <p className="mod-note">Not started.</p>}

      <h2 className="admin-section">Passport</h2>
      <dl className="mod-fields">
        <dt>Documents</dt><dd>{count(passport.documents)}</dd>
        <dt>Active shares</dt><dd>{count(passport.shares_active)}</dd>
        <dt>All shares</dt><dd>{count(passport.shares_total)}</dd>
      </dl>

      <h2 className="admin-section">Messages</h2>
      <dl className="mod-fields">
        <dt>Conversations</dt><dd>{count(messages.conversations)}</dd>
        <dt>Messages sent</dt><dd>{count(messages.sent)}</dd>
        <dt>Last message</dt><dd>{when(messages.last_message_at)}</dd>
      </dl>
      <p className="mod-note">Message text isn't shown.</p>

      <h2 className="admin-section">Safety</h2>
      <dl className="mod-fields">
        <dt>Reports they made</dt><dd>{count(safety.reports_made)}</dd>
        <dt>Reports about them</dt><dd>{count(safety.reports_about)}</dd>
        <dt>People they blocked</dt><dd>{count(safety.blocks_made)}</dd>
        <dt>Blocked by</dt><dd>{count(safety.blocked_by)}</dd>
        <dt>Appeals</dt><dd>{count(safety.appeals)}</dd>
        <dt>Suspensions</dt><dd>{count(safety.suspensions)}</dd>
      </dl>

      <h2 className="admin-section">Notifications</h2>
      <dl className="mod-fields">
        <dt>Phones with alerts on</dt><dd>{count(notifications.devices)}</dd>
        <dt>Unread alerts</dt><dd>{count(notifications.unread)}</dd>
      </dl>

      <h2 className="admin-section">Terms accepted</h2>
      <ListTable columns={termColumns} rows={terms}/>

      <h2 className="admin-section">Who has viewed this record</h2>
      <ListTable columns={viewColumns} rows={views}/>
    </>
  );
}

const landlordLines = (landlord) => (landlord ? [landlord.kind ? plainWords(landlord.kind) : '', landlord.name, landlord.agency, landlord.email, landlord.phone].filter(Boolean) : []);
const accommodationTypes = (home) => (Array.isArray(home.accommodation_types) ? home.accommodation_types.map(plainWords) : home.accommodation_types);

const privateHomeColumns = (homeName) => [
  { key: 'home', label: 'Home', value: (home) => homeName(home.id), cell: (home) => homeName(home.id) },
  { key: 'address', label: 'Address', value: (home) => home.address_line, cell: (home) => orDash(home.address_line) },
  { key: 'postcode', label: 'Postcode', value: (home) => home.postcode, cell: (home) => orDash(home.postcode) },
  { key: 'tenancy', label: 'Tenancy', value: (home) => labelOf(home.tenure, plainWords), cell: (home) => plainWords(home.tenure) },
  {
    key: 'type',
    label: 'Type of home',
    value: accommodationTypes,
    cell: (home) => orDash(Array.isArray(home.accommodation_types) ? accommodationTypes(home).join(', ') : home.accommodation_types),
  },
  {
    key: 'landlord',
    label: 'Landlord or agent',
    // Sorted by who they are (name, else agency, email or phone), not by the "Landlord" or "Agent" line above it.
    value: (home) => [home.landlord?.name, home.landlord?.agency, home.landlord?.email, home.landlord?.phone].find(Boolean),
    cell: (home) => {
      const lines = landlordLines(home.landlord || null);
      return lines.length ? lines.map((line, index) => <div key={index}>{line}</div>) : '–';
    },
  },
];

/** Only drawn when the admin asked for it: phone numbers, addresses, dates of birth, landlords and what they wrote. */
function PrivateDetails({ hidden, homes, repairs, homeJobs }) {
  const privateHomes = list(hidden.homes);
  const privateRepairs = list(hidden.repairs);
  const privateJobs = list(hidden.home_jobs);
  const homeName = (homeId) => orDash(homes.find((home) => home.id === homeId)?.label);
  const repairName = (repairId) => orDash(repairs.find((repair) => repair.id === repairId)?.title);
  const jobName = (jobId) => orDash(homeJobs.find((job) => job.id === jobId)?.title);
  const subheading = { fontSize: '.95rem' };

  return (
    <>
      <h2 className="admin-section">Private details</h2>
      <p className="mod-note">You asked to see these. Each time you do, it is recorded.</p>
      <dl className="mod-fields">
        <dt>Phone (sign-up)</dt><dd>{orDash(hidden.phone)}</dd>
        <dt>Date of birth</dt><dd>{day(hidden.date_of_birth)}</dd>
        <dt>Passport address</dt><dd>{orDash(hidden.passport_address)}</dd>
        <dt>Passport postcode</dt><dd>{orDash(hidden.passport_postcode)}</dd>
        <dt>Passport phone</dt><dd>{orDash(hidden.passport_phone)}</dd>
        <dt>ID type</dt><dd>{hidden.id_type ? (idTypeLabels[hidden.id_type] || plainWords(hidden.id_type)) : '–'}</dd>
      </dl>

      <h3 className="admin-section" style={subheading}>Home addresses and landlords</h3>
      <ListTable columns={privateHomeColumns(homeName)} rows={privateHomes}/>

      <h3 className="admin-section" style={subheading}>What they wrote in repairs</h3>
      {privateRepairs.length === 0
        ? <None/>
        : (
          <dl className="mod-fields">
            {privateRepairs.map((repair, index) => (
              <Fragment key={repair.id || index}>
                <dt>{repairName(repair.id)}</dt><dd>{orDash(repair.description)}</dd>
              </Fragment>
            ))}
          </dl>
        )}

      <h3 className="admin-section" style={subheading}>Job addresses and what they wrote</h3>
      {privateJobs.length === 0
        ? <None/>
        : (
          <dl className="mod-fields">
            {privateJobs.map((job, index) => (
              <Fragment key={job.id || index}>
                <dt>{jobName(job.id)}</dt><dd>{[job.exact_address, job.description].filter(Boolean).join('\n') || '–'}</dd>
              </Fragment>
            ))}
          </dl>
        )}
    </>
  );
}
